import { addSeconds } from "date-fns/addSeconds";

import {
  fromCurrentToGivenTimezone,
  getTimezone,
  nextQuarterHour,
  parseDate,
  swapTimezones,
} from "shared/helpers/date";
import type ScheduledReportSpecificationSchedule from "shared/resources/ScheduledReportSpecificationSchedule";

const MAX_FORTNIGHTLY_SEARCH_DAYS = 730;

export function getNextDeliveryDateTime(): Date {
  return nextQuarterHour();
}

export function getNextScheduleDeliveryDateTime(
  schedule: ScheduledReportSpecificationSchedule,
  timezone: string
): Date {
  switch (schedule.frequency) {
    case "once":
      return parseDate(schedule.deliverAt);
    case "daily":
      return nextDailyDeliveryDateTime(schedule, timezone);
    case "weekly":
      return nextWeeklyDeliveryDateTime(schedule, timezone);
    case "monthly":
      return nextMonthlyDeliveryDateTime(schedule, timezone);
    case "fortnightly":
      return nextFortnightlyDeliveryDateTime(schedule, timezone);
    default:
      return nextDailyDeliveryDateTime(schedule, timezone);
  }
}

function createDeliveryDateTime(
  schedule: ScheduledReportSpecificationSchedule,
  date: Date,
  timezone: string
): Date {
  return createDeliveryDateTimeAt(date, timezone, schedule.deliverAt);
}

function createDeliveryDateTimeAt(
  date: Date,
  timezone: string,
  deliveryAt: number
): Date {
  let deliveryDate = new Date(date.getFullYear(), date.getMonth());
  deliveryDate.setDate(date.getDate());
  deliveryDate = addSeconds(deliveryDate, deliveryAt);

  return swapTimezones(deliveryDate, timezone, getTimezone());
}

function nextWeeklyDeliveryDateTimeForDate(
  schedule: ScheduledReportSpecificationSchedule,
  date: Date,
  timezone: string,
  now: Date
): Date {
  const start = createDeliveryDateTime(schedule, date, timezone);

  if (
    start > now ||
    !schedule.deliverEvery ||
    schedule.deliverTo <= schedule.deliverAt
  ) {
    return start;
  }

  for (
    let deliveryAt = schedule.deliverAt + schedule.deliverEvery;
    deliveryAt <= schedule.deliverTo;
    deliveryAt += schedule.deliverEvery
  ) {
    const deliveryDateTime = createDeliveryDateTimeAt(
      date,
      timezone,
      deliveryAt
    );

    if (deliveryDateTime > now) {
      return deliveryDateTime;
    }
  }

  return start;
}

function monthlyDeliveryDays(
  schedule: ScheduledReportSpecificationSchedule,
  year: number,
  month: number
): number[] {
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

  const deliveryDays = new Set([
    ...(schedule.daysOfMonth || []),
    ...(schedule.onLastDay ? [lastDayOfMonth] : []),
  ]);

  return [...deliveryDays]
    .filter((day) => day > 0 && day <= lastDayOfMonth)
    .sort((firstDay, secondDay) => firstDay - secondDay);
}

function nextDailyDeliveryDateTime(
  schedule: ScheduledReportSpecificationSchedule,
  timezone: string
): Date {
  const now = new Date();
  const nowInTimezone = fromCurrentToGivenTimezone(now, timezone);

  const todayDeliveryDateTime = createDeliveryDateTime(
    schedule,
    nowInTimezone,
    timezone
  );

  if (todayDeliveryDateTime > now) {
    return todayDeliveryDateTime;
  }

  const tomorrowInTimezone = new Date(nowInTimezone);
  tomorrowInTimezone.setDate(nowInTimezone.getDate() + 1);

  return createDeliveryDateTime(schedule, tomorrowInTimezone, timezone);
}

function nextFortnightlyDeliveryDateTime(
  schedule: ScheduledReportSpecificationSchedule,
  timezone: string
): Date {
  if (!schedule.fortnightStartDate) {
    return nextDailyDeliveryDateTime(schedule, timezone);
  }

  const now = new Date();

  const startDateTime = fromCurrentToGivenTimezone(
    new Date(schedule.fortnightStartDate),
    timezone
  );

  const candidateDateTime = Array.from(
    { length: Math.floor(MAX_FORTNIGHTLY_SEARCH_DAYS / 14) + 1 },
    (_item, index) => index * 14
  )
    .map((dayOffset) => {
      const candidate = new Date(startDateTime);
      candidate.setDate(startDateTime.getDate() + dayOffset);

      return createDeliveryDateTime(schedule, candidate, timezone);
    })
    .find((candidate) => candidate > now);

  return candidateDateTime ?? nextDailyDeliveryDateTime(schedule, timezone);
}

function nextMonthlyDeliveryDateTime(
  schedule: ScheduledReportSpecificationSchedule,
  timezone: string
): Date {
  const now = new Date();
  const nowInTimezone = fromCurrentToGivenTimezone(now, timezone);

  const candidateDateTime = Array.from({ length: 25 }, (_item, monthOffset) => {
    const candidateMonth = new Date(
      nowInTimezone.getFullYear(),
      nowInTimezone.getMonth() + monthOffset,
      1
    );

    return monthlyDeliveryDays(
      schedule,
      candidateMonth.getFullYear(),
      candidateMonth.getMonth()
    ).map((candidateDay) => {
      const candidateDate = new Date(candidateMonth);
      candidateDate.setDate(candidateDay);

      return createDeliveryDateTime(schedule, candidateDate, timezone);
    });
  })
    .flat()
    .find((candidate) => candidate > now);

  return candidateDateTime ?? nextDailyDeliveryDateTime(schedule, timezone);
}

function nextWeeklyDeliveryDateTime(
  schedule: ScheduledReportSpecificationSchedule,
  timezone: string
): Date {
  const selectedWeekdayIndexes = new Set(
    [
      { enabled: schedule.onSunday, index: 0 },
      { enabled: schedule.onMonday, index: 1 },
      { enabled: schedule.onTuesday, index: 2 },
      { enabled: schedule.onWednesday, index: 3 },
      { enabled: schedule.onThursday, index: 4 },
      { enabled: schedule.onFriday, index: 5 },
      { enabled: schedule.onSaturday, index: 6 },
    ]
      .filter(({ enabled }) => enabled)
      .map(({ index }) => index)
  );

  if (!selectedWeekdayIndexes.size) {
    return nextDailyDeliveryDateTime(schedule, timezone);
  }

  const now = new Date();
  const nowInTimezone = fromCurrentToGivenTimezone(now, timezone);

  const candidateDateTime = Array.from({ length: 15 }, (_item, dayOffset) => {
    const candidateDate = new Date(nowInTimezone);
    candidateDate.setDate(nowInTimezone.getDate() + dayOffset);

    if (!selectedWeekdayIndexes.has(candidateDate.getDay())) {
      return undefined;
    }

    const candidate = nextWeeklyDeliveryDateTimeForDate(
      schedule,
      candidateDate,
      timezone,
      now
    );

    return candidate > now ? candidate : undefined;
  }).find((candidate) => candidate);

  return candidateDateTime ?? nextDailyDeliveryDateTime(schedule, timezone);
}
